import { reconcile, type SyncSnapshot } from './reconcile';

export interface SyncRecord { type: string; id: string; value: SyncSnapshot }
export interface RemoteRecord extends SyncRecord { version: number }
export interface SyncBaseline { value: SyncSnapshot; version: number }
export interface PendingChange { operationId: string; type: string; id: string; baseVersion: number; value: SyncSnapshot }
export interface SyncMetadata { owner: string; records: Record<string, SyncBaseline>; pending: PendingChange | null }
export interface SyncConflict { key: string; local: SyncSnapshot; remote: SyncSnapshot; version: number }
export interface SafeSyncPorts {
  readLocal(): Promise<SyncRecord[]>;
  readRemote(): Promise<RemoteRecord[]>;
  loadMetadata(): Promise<SyncMetadata | null>;
  saveMetadata(value: SyncMetadata): Promise<void>;
  saveRecoveryPoint(): Promise<void>;
  // Must compare the present local value and write in one transaction where supported.
  applyIfUnchanged(record: SyncRecord, expected: SyncSnapshot): Promise<boolean>;
  // Idempotent by operationId; stale baseVersion must return a conflict.
  push(change: PendingChange): Promise<{ applied: boolean; version: number }>;
  newId(): string;
  requiresReview?(record: SyncRecord, current: SyncSnapshot): boolean;
  checkCancelled?(): void;
  deferDownload?(record: SyncRecord): boolean;
}
export interface SafeSyncResult { uploaded: number; downloaded: number; conflicts: SyncConflict[]; deferred?: number; settled?: boolean }
const keyOf = (record: Pick<SyncRecord, 'type' | 'id'>) => JSON.stringify([record.type, record.id]);
function indexed<T extends SyncRecord>(records: T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const record of records) {
    const key = keyOf(record);
    if (map.has(key)) throw new Error('Повторяющаяся запись при синхронизации.');
    map.set(key, record);
  }
  return map;
}
/**
 * A run is serialized by its caller. Network, local writes and metadata cannot be
 * atomic together, so each upload is journalled before sending; downloads only
 * occur after a durable recovery point and an atomic compare against the read value.
 */
export async function safeSync(userId: string, ports: SafeSyncPorts): Promise<SafeSyncResult> {
  ports.checkCancelled?.();
  let metadata = await ports.loadMetadata();
  if (metadata && metadata.owner !== userId) throw new Error('На устройстве данные другого аккаунта. Синхронизация остановлена.');
  if (!metadata) { metadata = { owner: userId, records: {}, pending: null }; await ports.saveMetadata(metadata); }
  const result: SafeSyncResult = { uploaded: 0, downloaded: 0, conflicts: [] };
  ports.checkCancelled?.();
  if (metadata.pending) {
    const pending = metadata.pending;
    const response = await ports.push(pending);
    if (response.applied) {
      metadata.records[keyOf(pending)] = { value: pending.value, version: response.version };
      metadata.pending = null; await ports.saveMetadata(metadata);
    } else {
      // Never guess whether a rejected write was applied. Pull current state below.
      metadata.pending = null; await ports.saveMetadata(metadata);
    }
  }
  ports.checkCancelled?.();
  const remote = indexed(await ports.readRemote());
  ports.checkCancelled?.();
  const local = indexed(await ports.readLocal());
  let recoverySaved = false;
  const keys = new Set([...local.keys(), ...remote.keys(), ...Object.keys(metadata.records)]);
  for (const key of keys) {
    ports.checkCancelled?.();
    const localEntry = local.get(key); const remoteEntry = remote.get(key);
    const previous = metadata.records[key];
    const localValue = localEntry?.value ?? null; const remoteValue = remoteEntry?.value ?? null;
    const version = remoteEntry?.version ?? 0;
    const target = localEntry ?? remoteEntry;
    // A baseline can outlive both sides after deletion; nothing needs applying.
    if (!target) continue;
    // A server tombstone is a known deletion, not an empty account. An old
    // device must not silently resurrect it on its first connection.
    // A missing previously acknowledged server record is not a deletion: the
    // server retains tombstones. Stop if its history was reset or truncated.
    if (previous && previous.version > 0 && !remoteEntry) {
      result.conflicts.push({key,local:localValue,remote:null,version:0}); continue;
    }
    const decision = !previous && remoteEntry && remoteValue === null && localValue !== null
      ? 'conflict' : reconcile(localValue, remoteValue, previous?.value);
    if (decision === 'conflict') { result.conflicts.push({ key, local: localValue, remote: remoteValue, version }); continue; }
    if (decision === 'unchanged') {
      if (!previous || previous.value !== remoteValue || previous.version !== version) { metadata.records[key] = { value: remoteValue, version }; await ports.saveMetadata(metadata); }
      continue;
    }
    if (decision === 'upload') {
      const change: PendingChange = { operationId: ports.newId(), type: target.type, id: target.id, baseVersion: version, value: localValue };
      metadata.pending = change; await ports.saveMetadata(metadata);
      const response = await ports.push(change);
      if (!response.applied) { metadata.pending = null; await ports.saveMetadata(metadata); result.conflicts.push({ key, local: localValue, remote: remoteValue, version }); continue; }
      metadata.records[key] = { value: localValue, version: response.version };
      metadata.pending = null; await ports.saveMetadata(metadata); result.uploaded++; continue;
    }
    if (ports.requiresReview?.({type:target.type,id:target.id,value:remoteValue},localValue)) {
      result.conflicts.push({key,local:localValue,remote:remoteValue,version}); continue;
    }
    if (ports.deferDownload?.({type:target.type,id:target.id,value:remoteValue})) { result.deferred=(result.deferred ?? 0)+1; continue; }
    if (!recoverySaved) { await ports.saveRecoveryPoint(); recoverySaved = true; }
    ports.checkCancelled?.();
    if (!await ports.applyIfUnchanged({ type: target.type, id: target.id, value: remoteValue }, localValue)) {
      result.conflicts.push({ key, local: localValue, remote: remoteValue, version }); continue;
    }
    metadata.records[key] = { value: remoteValue, version }; await ports.saveMetadata(metadata); result.downloaded++;
  }
  return result;
}
